/* eslint-disable max-lines-per-function */
import { useQueryClient } from '@tanstack/react-query';
import {
  ChartAssetData,
  Colors,
  defaultColors,
  getPrimaryDetails,
  isIntradayResample,
  PaneData,
  RangeHorizon,
  ResampleIntervals,
} from '@toggle/chart';
import { getTimeTillEndOfDayMs, isSnakeWithThreshold } from '@toggle/helpers';
import { Entity, SnakeMeta } from '@toggle/toggle';
import { Dispatch, SetStateAction } from 'react';
import { v4 } from 'uuid';

import { postEntities } from '~/api/entities/entity-service';
import { fetchSnakeByName } from '~/services/overview-widget/overview-widget-service';

import {
  createChartDataItem,
  createTsDataFromSnake,
  getCurrentLineColor,
  getLineColor,
  paneContainsAsset,
  updateAllPanes,
} from '../../utils/chart-utils/chart-utils';
import {
  getAssetEmptyResamples,
  getChartDataEmptyResamples,
} from '../../utils/resample-utils/resample-utils';
import { UseChartStateReturn } from '../use-chart-state/useChartState';
import { UseResampleReturn } from '../use-resample/useResample';

export interface UseChartPaneProps {
  setAssetWithError: Dispatch<SetStateAction<Entity | undefined>>;
  setHasError: Dispatch<SetStateAction<boolean>>;
  primaryAsset?: ChartAssetData;
  chartPanes: PaneData[];
  colors: Colors;
  resample: UseResampleReturn['resample'];
  updateChartState: UseChartStateReturn['updateChartState'];
  handleEmptyChart: (bannerLabel: string) => void;
}

export type UseChartPaneReturn = ReturnType<typeof useChartPane>;

export const useChartPane = ({
  setAssetWithError,
  setHasError,
  handleEmptyChart,
  primaryAsset,
  chartPanes,
  colors,
  updateChartState,
  resample,
}: UseChartPaneProps) => {
  const queryClient = useQueryClient();

  const addIndicatorPane = async ({
    indicator,
    asset,
    assetToChange,
  }: {
    indicator: SnakeMeta;
    asset: Entity;
    assetToChange?: Entity;
  }) => {
    try {
      setHasError(false);
      const snakeData = await fetchSnakeByName(indicator.snake);
      if (snakeData.error) {
        throw snakeData.error;
      }

      const prices = createTsDataFromSnake(snakeData.data.result.data);

      if (!prices.length) {
        setAssetWithError(asset);
        handleEmptyChart(asset.name);
        return;
      }

      const paneIndex = assetToChange
        ? chartPanes.findIndex(p =>
            paneContainsAsset(p, { entity: assetToChange })
          )
        : -1;
      const newLineColor = isSnakeWithThreshold(indicator)
        ? defaultColors.white
        : getLineColor({
            colors,
            chartData: chartPanes
              .slice(0, chartPanes.length)
              .flatMap(pane => pane.chartAssetData),
          });

      const newChartData = createChartDataItem({
        entity: {
          ...asset,
          default_snake: indicator.snake,
          currency: indicator.currency,
        },
        ts: prices,
        lineColor: assetToChange
          ? getCurrentLineColor({
              chartData: chartPanes[paneIndex].chartAssetData,
              snake: assetToChange.default_snake,
              colors,
            })
          : newLineColor,
        snakeMeta: indicator,
        primaryAsset,
      });

      let newChartPanes: PaneData[] = [...chartPanes];

      if (assetToChange) {
        newChartPanes[paneIndex].chartAssetData = newChartPanes[
          paneIndex
        ].chartAssetData.map(c =>
          c.entity.default_snake === assetToChange.default_snake
            ? newChartData
            : c
        );
      } else {
        newChartPanes.push({
          id: v4(),
          chartAssetData: [newChartData],
          seriesType: 'line',
          priceDisplay: 'price',
          yAxisType: 'merged',
        });
      }

      resample.setEmptyResamples(
        getAssetEmptyResamples(
          primaryAsset?.entity ?? newChartData.entity,
          true
        )
      );
      const optionalHorizon = isIntradayResample(resample.selectedResample)
        ? { horizon: RangeHorizon.ThreeMonths }
        : {};

      updateChartState({
        chartPanes: newChartPanes,
        resample: ResampleIntervals.OneDay,
        ...optionalHorizon,
      });
    } catch (error) {
      setHasError(true);
    }
  };

  const addToggleIndicator = async (indicator: SnakeMeta, checked: boolean) => {
    setHasError(false);
    const staleTime = getTimeTillEndOfDayMs();

    if (!checked) {
      const pane = chartPanes.find(p =>
        p.chartAssetData.some(a => a.snakeMeta?.snake === indicator.snake)
      ) as PaneData;
      removePane(pane.id);
      return;
    }

    try {
      //if it's intraday resample we have to fetch 1D resample data and update ALL the panes
      //otherwise just add new pane with indicator
      const isIntraday = isIntradayResample(resample.selectedResample);
      const chartData = isIntraday
        ? chartPanes.flatMap(p => p.chartAssetData)
        : [];
      const [snakeData, entityData, newChartAssetData] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['tli', indicator.snake],
          queryFn: () => fetchSnakeByName(indicator.snake),
          staleTime,
        }),
        queryClient.fetchQuery({
          queryKey: ['tli', indicator.entity],
          queryFn: () => postEntities({ filter: 2, args: [indicator.entity] }),
          staleTime,
        }),
        chartData.length
          ? resample.fetchResampleIfNeeded(chartData, ResampleIntervals.OneDay)
          : Promise.resolve([]),
      ]);
      const error = snakeData.error || entityData.error;
      if (error) {
        throw error;
      }

      const updatedPanes = newChartAssetData.length
        ? updateAllPanes(newChartAssetData, chartPanes)
        : chartPanes;
      const primaryDetails = getPrimaryDetails(updatedPanes);
      const prices = createTsDataFromSnake(snakeData.data.result.data);
      const newChartData = createChartDataItem({
        entity: {
          ...entityData.data[0],
          default_snake: indicator.snake,
        },
        ts: prices,
        lineColor: defaultColors.white,
        primaryAsset: primaryDetails.primaryAsset,
        snakeMeta: indicator,
      });

      const newChartPanes: PaneData[] = [
        ...updatedPanes,
        {
          id: v4(),
          chartAssetData: [newChartData],
          seriesType: 'line',
          priceDisplay: 'price',
          yAxisType: 'merged',
        },
      ];

      resample.setEmptyResamples(
        getAssetEmptyResamples(
          primaryDetails.primaryAsset?.entity ?? newChartData.entity,
          true
        )
      );

      updateChartState({
        chartPanes: newChartPanes,
        resample: ResampleIntervals.OneDay,
        ...(isIntraday && { horizon: RangeHorizon.ThreeMonths }),
      });
    } catch (error) {
      setHasError(true);
    }
  };

  const removePane = (id: string) => {
    let nextChartPanes = chartPanes.filter(pane => pane.id !== id);
    resample.setEmptyResamples(
      getChartDataEmptyResamples(nextChartPanes.flatMap(p => p.chartAssetData))
    );
    const collapsedPanes = chartPanes.filter(p => p.collapsed);
    if (nextChartPanes.length === collapsedPanes.length) {
      nextChartPanes = [
        { ...nextChartPanes[0], collapsed: false },
        ...nextChartPanes.slice(1),
      ];
    }
    updateChartState({ chartPanes: nextChartPanes });
  };

  const movePane = (direction: 'up' | 'down', paneIndex: number) => {
    const nextChartPanes = [...chartPanes];
    const moveDownIndex =
      paneIndex !== chartPanes.length - 1 ? paneIndex + 1 : paneIndex;
    let newIndex =
      direction === 'up' && paneIndex !== 0 ? paneIndex - 1 : moveDownIndex;

    [nextChartPanes[newIndex], nextChartPanes[paneIndex]] = [
      nextChartPanes[paneIndex],
      nextChartPanes[newIndex],
    ];

    updateChartState({ chartPanes: nextChartPanes });
  };

  const moveBetweenPanes = ({
    newPaneIndex,
    paneIndex,
    snake,
    inExistingPane = true,
  }: {
    paneIndex: number;
    newPaneIndex: number;
    snake: string;
    inExistingPane?: boolean;
    panelLength?: number;
  }) => {
    let nextChartPanes = [...chartPanes];
    const updatedPane = nextChartPanes[newPaneIndex];
    const currentChartAssetData = nextChartPanes[paneIndex].chartAssetData;

    const movingChartAsset = currentChartAssetData.find(
      chartData => chartData.entity.default_snake === snake
    ) as ChartAssetData;

    nextChartPanes[paneIndex].chartAssetData = currentChartAssetData.filter(
      chartData => chartData.entity.default_snake !== snake
    );

    if (!nextChartPanes[paneIndex].chartAssetData.length) {
      nextChartPanes = nextChartPanes.filter(
        p => p.id !== nextChartPanes[paneIndex].id
      );
    }

    if (updatedPane && inExistingPane) {
      updatedPane.chartAssetData = [
        ...updatedPane.chartAssetData,
        movingChartAsset,
      ];
    } else {
      const newChartPane: PaneData = {
        id: v4(),
        chartAssetData: [movingChartAsset],
        seriesType: 'line',
        priceDisplay: 'price',
        yAxisType: 'merged',
      };

      nextChartPanes = [
        ...nextChartPanes.slice(0, Math.max(0, newPaneIndex)),
        newChartPane,
        ...nextChartPanes.slice(newPaneIndex + Number(inExistingPane)),
      ];
    }

    updateChartState({ chartPanes: nextChartPanes });
  };

  const updatePaneField =
    (
      field: Extract<keyof PaneData, 'collapsed' | 'maximized'>,
      paneId: string
    ) =>
    (pane: PaneData) =>
      pane.id === paneId ? { ...pane, [field]: !pane[field] } : pane;

  const collapsePane = (id: string) => {
    const nextChartPanes = chartPanes.map(updatePaneField('collapsed', id));
    updateChartState({ chartPanes: nextChartPanes });
  };

  const maximizePane = (id: string) => {
    const nextChartPanes = chartPanes.map(updatePaneField('maximized', id));
    updateChartState({ chartPanes: nextChartPanes });
  };

  return {
    removePane,
    movePane,
    addIndicatorPane,
    addToggleIndicator,
    collapsePane,
    maximizePane,
    moveBetweenPanes,
  };
};
